import React, { useEffect, useState } from "react";
import {
  FaStore,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaCheckCircle,
  FaCalendarAlt,
  FaCity,
  FaEnvelope,
  FaEdit,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaTimes,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import "./PharmacyProfile.css";
import { cloudApi } from "../../api";

const PharmacyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [logoVersion, setLogoVersion] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const [userType, setUserType] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateConfirmation, setDeactivateConfirmation] = useState("");
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState("");
  const [deactivateSuccess, setDeactivateSuccess] = useState("");

  const [showPhoneSection, setShowPhoneSection] = useState(false);
  const [phoneStep, setPhoneStep] = useState(1);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState("");
  const [isRequestingPhoneOtp, setIsRequestingPhoneOtp] = useState(false);
  const [isConfirmingPhone, setIsConfirmingPhone] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("access_token");

  const isPharmacyOwner = userType === "PHARMACY";

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserType(decoded.user_type?.trim() || "");
      } catch {
        setUserType("");
      }
    }
  }, [token]);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get("/users/profile");
      setProfile(data.profile);
      setError("");
    } catch (err) {
      console.error("Profile fetch error:", err);
      setError("Failed to load profile.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleVerificationRequest = () => navigate("/PharmacyVerificationForm");
  const handleUpdateProfile = () => navigate("/UpdatePharmacyProfile");

  const handleLogoChange = async (e) => {
    if (!isOnline) {
      alert("Logo update requires an internet connection.");
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("logo", file);
    try {
      await cloudApi.put("/users/pharmacy/logo", formData);
      alert("Logo updated successfully!");
      setLogoVersion(Date.now());
      fetchProfile();
    } catch (err) {
      console.error("Logo upload error:", err);
      alert("Failed to update logo.");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!isOnline) {
      setPasswordError("No internet connection. Please check your network and try again.");
      return;
    }
    if (!newPassword || !confirmPassword) {
      setPasswordError("Please fill in all password fields.");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setIsChangingPassword(true);
    try {
      const formData = new FormData();
      formData.append("new_password", newPassword);
      await cloudApi.post("/auth/change-password", formData);
      setPasswordSuccess("Password changed successfully!");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setShowPasswordSection(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err) {
      console.error("Password change error:", err);
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setPasswordError("No internet connection. Please check your network and try again.");
      } else {
        setPasswordError(err.response?.data?.detail || "Failed to change password.");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleRequestPhoneOtp = async (e) => {
    e.preventDefault();
    setPhoneError("");
    setPhoneSuccess("");

    if (!isOnline) {
      setPhoneError("No internet connection. Please check your network and try again.");
      return;
    }
    if (!newPhoneNumber.trim()) {
      setPhoneError("Please enter a phone number.");
      return;
    }
    if (!/^\d+$/.test(newPhoneNumber)) {
      setPhoneError("Phone number must contain only digits.");
      return;
    }
    if (newPhoneNumber.length < 10 || newPhoneNumber.length > 15) {
      setPhoneError("Phone number must be between 10 and 15 digits.");
      return;
    }
    if (newPhoneNumber === profile?.phone_number) {
      setPhoneError("New phone number is the same as your current one.");
      return;
    }

    setIsRequestingPhoneOtp(true);
    try {
      const formData = new FormData();
      formData.append("new_phone_number", newPhoneNumber);
      await cloudApi.post("/users/users/request-phone-change", formData);
      setPhoneSuccess(`OTP sent to your registered email (${profile?.email || "your email"}). Enter it below to confirm.`);
      setPhoneStep(2);
    } catch (err) {
      console.error("Phone OTP request error:", err);
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setPhoneError("No internet connection. Please check your network and try again.");
      } else {
        setPhoneError(err.response?.data?.detail || "Failed to send OTP. Please try again.");
      }
    } finally {
      setIsRequestingPhoneOtp(false);
    }
  };

  const handleConfirmPhoneChange = async (e) => {
    e.preventDefault();
    setPhoneError("");
    setPhoneSuccess("");

    if (!isOnline) {
      setPhoneError("No internet connection. Please check your network and try again.");
      return;
    }
    if (!phoneOtp.trim()) {
      setPhoneError("Please enter the OTP.");
      return;
    }

    setIsConfirmingPhone(true);
    try {
      const formData = new FormData();
      formData.append("new_phone_number", newPhoneNumber);
      formData.append("otp_code", phoneOtp);
      await cloudApi.put("/users/users/phone-number", formData);

      setPhoneSuccess("Phone number updated successfully!");
      setProfile((prev) => ({ ...prev, phone_number: newPhoneNumber }));

      setTimeout(() => {
        setShowPhoneSection(false);
        setPhoneStep(1);
        setNewPhoneNumber("");
        setPhoneOtp("");
        setPhoneSuccess("");
      }, 2000);
    } catch (err) {
      console.error("Phone confirm error:", err);
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setPhoneError("No internet connection. Please check your network and try again.");
      } else {
        setPhoneError(err.response?.data?.detail || "Invalid or expired OTP. Please try again.");
      }
    } finally {
      setIsConfirmingPhone(false);
    }
  };

  const handleCancelPhoneChange = () => {
    setShowPhoneSection(false);
    setPhoneStep(1);
    setNewPhoneNumber("");
    setPhoneOtp("");
    setPhoneError("");
    setPhoneSuccess("");
  };

  const handleDeactivatePharmacy = async () => {
    setDeactivateError("");
    setDeactivateSuccess("");

    if (deactivateConfirmation !== "DEACTIVATE") {
      setDeactivateError('Please type "DEACTIVATE" to confirm.');
      return;
    }

    setIsDeactivating(true);
    try {
      const response = await cloudApi.post("/users/deactivate-pharmacy/");
      setDeactivateSuccess(response.data.message);
      setDeactivateConfirmation("");
      setShowDeactivateModal(false);
      setTimeout(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Deactivation error:", err);
      setDeactivateError(err.response?.data?.detail || "Failed to deactivate pharmacy.");
    } finally {
      setIsDeactivating(false);
    }
  };

  const statusClass = (status) =>
    ({
      VERIFIED: "verified-status",
      REJECTED: "rejected-status",
      PENDING: "pending-status",
      NOT_APPLIED: "not-applied-status",
    }[status] || "");

  if (isLoading) {
    return (
      <div className="profile-layout">
        {!isMobile && <Sidebar />}
        <div className="profile-main">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-layout">
      {!isMobile && <Sidebar />}

      <div className="profile-main">

        {!isOnline && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            fontSize: "0.9rem",
          }}>
            <span style={{ fontSize: "1.3rem" }}>📡</span>
            <div>
              <strong style={{ color: "#856404" }}>You are offline</strong>
              <p style={{ margin: 0, color: "#856404", fontSize: "0.85rem" }}>
                Profile is shown from local cache. Password change, phone number
                update, and pharmacy deactivation require an internet connection.
              </p>
            </div>
          </div>
        )}

        {error ? (
          <div className="error-message">{error}</div>
        ) : profile ? (
          <div className="profile-card fade-in">
            <div className="logo-wrapper">
              <img
                src={`${profile.logo || `${process.env.REACT_APP_API_BASE_URL}/static/images/profile.png`}?v=${logoVersion}`}
                alt="Pharmacy Logo"
                className="pharmacy-logo"
              />
            </div>

            {isPharmacyOwner && (
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="logo-input"
              />
            )}

            <h3 className="pharmacy-name">
              <FaStore /> {profile.pharmacy_name || profile.branch_name || "—"}{" "}
              {profile.verification_status === "VERIFIED" && (
                <FaCheckCircle className="verified-icon" />
              )}
            </h3>

            <div className="profile-info">
              {/* ── Address — covers pharmacy owner, branch user, and staff ── */}
              <p>
                <FaMapMarkerAlt />{" "}
                {profile.pharmacy_address || profile.branch_address || profile.address || "—"}
              </p>
              <p><FaCity /> {profile.city || "N/A"}</p>

              <p>
                <FaPhoneAlt /> {profile.phone_number || "—"}
                {isPharmacyOwner && (
                  <button
                    onClick={() => {
                      if (!isOnline) {
                        setPhoneError("Phone number change requires an internet connection.");
                        setShowPhoneSection(false);
                        return;
                      }
                      setPhoneError("");
                      setShowPhoneSection(!showPhoneSection);
                      if (showPhoneSection) handleCancelPhoneChange();
                    }}
                    style={{
                      marginLeft: "0.6rem",
                      padding: "0.15rem 0.55rem",
                      fontSize: "0.75rem",
                      background: showPhoneSection ? "#6c757d" : "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: 500,
                      verticalAlign: "middle",
                    }}
                  >
                    {showPhoneSection ? "Cancel" : "📱 Change"}
                  </button>
                )}
              </p>

              {showPhoneSection && isPharmacyOwner && (
                <div style={{
                  marginTop: "0.75rem",
                  padding: "1rem",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                }}>
                  <p style={{ fontSize: "0.8rem", color: "#666", marginBottom: "0.75rem" }}>
                    Step {phoneStep} of 2 — {phoneStep === 1 ? "Enter new phone number" : "Enter OTP sent to your email"}
                  </p>

                  {phoneError && (
                    <div style={{
                      padding: "0.5rem 0.75rem",
                      background: "#fff5f5",
                      border: "1px solid #dc3545",
                      borderRadius: "5px",
                      color: "#dc3545",
                      fontSize: "0.85rem",
                      marginBottom: "0.75rem",
                    }}>
                      {phoneError}
                    </div>
                  )}
                  {phoneSuccess && (
                    <div style={{
                      padding: "0.5rem 0.75rem",
                      background: "#f0fff4",
                      border: "1px solid #28a745",
                      borderRadius: "5px",
                      color: "#28a745",
                      fontSize: "0.85rem",
                      marginBottom: "0.75rem",
                    }}>
                      ✅ {phoneSuccess}
                    </div>
                  )}

                  {phoneStep === 1 && (
                    <form onSubmit={handleRequestPhoneOtp}>
                      <input
                        type="tel"
                        value={newPhoneNumber}
                        onChange={(e) => { setNewPhoneNumber(e.target.value); setPhoneError(""); }}
                        placeholder="New phone number (e.g. 08012345678)"
                        disabled={isRequestingPhoneOtp}
                        style={{
                          width: "100%",
                          padding: "0.6rem 0.75rem",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                          fontSize: "0.9rem",
                          boxSizing: "border-box",
                          marginBottom: "0.6rem",
                        }}
                      />
                      <button
                        type="submit"
                        disabled={isRequestingPhoneOtp || !newPhoneNumber.trim()}
                        style={{
                          padding: "0.55rem 1.2rem",
                          background: isRequestingPhoneOtp ? "#aaa" : "#17a2b8",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          fontSize: "0.9rem",
                          cursor: isRequestingPhoneOtp ? "not-allowed" : "pointer",
                          fontWeight: 600,
                          width: "100%",
                        }}
                      >
                        {isRequestingPhoneOtp ? "Sending OTP..." : "Send OTP to Email"}
                      </button>
                    </form>
                  )}

                  {phoneStep === 2 && (
                    <form onSubmit={handleConfirmPhoneChange}>
                      <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: "0.6rem" }}>
                        New number: <strong>{newPhoneNumber}</strong>
                      </p>
                      <input
                        type="text"
                        value={phoneOtp}
                        onChange={(e) => { setPhoneOtp(e.target.value); setPhoneError(""); }}
                        placeholder="Enter OTP from your email"
                        disabled={isConfirmingPhone}
                        autoFocus
                        style={{
                          width: "100%",
                          padding: "0.6rem 0.75rem",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                          fontSize: "0.9rem",
                          boxSizing: "border-box",
                          marginBottom: "0.6rem",
                          letterSpacing: "0.15rem",
                        }}
                      />
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          type="button"
                          onClick={() => { setPhoneStep(1); setPhoneOtp(""); setPhoneError(""); setPhoneSuccess(""); }}
                          disabled={isConfirmingPhone}
                          style={{
                            flex: 1,
                            padding: "0.55rem",
                            background: "white",
                            color: "#555",
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            fontSize: "0.85rem",
                            cursor: "pointer",
                          }}
                        >
                          ← Back
                        </button>
                        <button
                          type="submit"
                          disabled={isConfirmingPhone || !phoneOtp.trim()}
                          style={{
                            flex: 2,
                            padding: "0.55rem",
                            background: isConfirmingPhone ? "#aaa" : "#28a745",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            fontSize: "0.9rem",
                            cursor: isConfirmingPhone ? "not-allowed" : "pointer",
                            fontWeight: 600,
                          }}
                        >
                          {isConfirmingPhone ? "Confirming..." : "✓ Confirm Change"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {!isOnline && phoneError && (
                <p style={{ color: "#dc3545", fontSize: "0.82rem", marginTop: "0.3rem" }}>
                  {phoneError}
                </p>
              )}

              <p><FaEnvelope /> {profile.email || "N/A"}</p>
              <p>
                Verification Status:{" "}
                <strong className={statusClass(profile.verification_status)}>
                  {profile.verification_status || "N/A"}
                </strong>
              </p>
              <p>
                <FaCalendarAlt /> Created:{" "}
                {profile.created_at
                  ? new Date(profile.created_at).toLocaleDateString()
                  : "—"}
              </p>
            </div>

            <div className="password-section">
              <button
                className="toggle-password-btn"
                onClick={() => {
                  if (!isOnline) {
                    setPasswordError("Password change requires an internet connection.");
                    setShowPasswordSection(false);
                    return;
                  }
                  setPasswordError("");
                  setShowPasswordSection(!showPasswordSection);
                }}
              >
                <FaLock />{" "}
                {showPasswordSection ? "Cancel Password Change" : "Change Password"}
              </button>

              {!isOnline && passwordError && (
                <p className="password-error" style={{ marginTop: "0.5rem" }}>
                  {passwordError}
                </p>
              )}

              {showPasswordSection && isOnline && (
                <form className="password-form" onSubmit={handleChangePassword}>
                  <div className="password-input-group">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New Password"
                      className="password-input"
                    />
                    <button
                      type="button"
                      className="toggle-password-visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>

                  <div className="password-input-group">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm Password"
                      className="password-input"
                    />
                    <button
                      type="button"
                      className="toggle-password-visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>

                  {passwordError && <p className="password-error">{passwordError}</p>}
                  {passwordSuccess && <p className="password-success">{passwordSuccess}</p>}
                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    className="submit-password-btn"
                  >
                    {isChangingPassword ? "Updating..." : "Update Password"}
                  </button>
                </form>
              )}
            </div>

            {(profile.verification_status === "NOT_APPLIED" ||
              profile.verification_status === "REJECTED") && (
              <button
                className="verification-btn"
                onClick={handleVerificationRequest}
              >
                Submit Verification Request
              </button>
            )}

            {isPharmacyOwner && (
              <button className="update-profile-btn" onClick={handleUpdateProfile}>
                <FaEdit /> Update Profile
              </button>
            )}

            {isPharmacyOwner && (
              <button
                className="deactivate-pharmacy-btn"
                disabled={!isOnline}
                title={!isOnline ? "Internet connection required to deactivate pharmacy" : undefined}
                style={!isOnline ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                onClick={() => {
                  if (!isOnline) return;
                  setShowDeactivateModal(true);
                }}
              >
                <FaTrash /> Deactivate Pharmacy
              </button>
            )}

            {isPharmacyOwner && !isOnline && (
              <p style={{
                fontSize: "0.78rem",
                color: "#856404",
                marginTop: "0.25rem",
                textAlign: "center",
              }}>
                Deactivation requires internet connection
              </p>
            )}
          </div>
        ) : (
          <div className="error-message">No profile data found.</div>
        )}
      </div>

      {showDeactivateModal && (
        <div className="modal-overlay" onClick={() => setShowDeactivateModal(false)}>
          <div className="modal-content deactivate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Deactivate Pharmacy</h2>
              <button className="modal-close-btn" onClick={() => setShowDeactivateModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="warning-box">
                <p className="warning-title">⚠️ Warning: This action cannot be undone immediately</p>
                <p className="warning-text">Deactivating your pharmacy will:</p>
                <ul className="warning-list">
                  <li>✓ Soft-delete your account</li>
                  <li>✓ Deactivate all pharmacy branches</li>
                  <li>✓ Deactivate all pharmacy staff</li>
                  <li>✓ Revoke all active sessions</li>
                  <li>✓ Prevent all users from logging in</li>
                  <li>✓ Preserve all data for compliance</li>
                </ul>
              </div>

              {deactivateError && (
                <div className="deactivate-error-message">{deactivateError}</div>
              )}
              {deactivateSuccess && (
                <div className="deactivate-success-message">{deactivateSuccess}</div>
              )}

              <div className="confirmation-section">
                <p className="confirmation-text">
                  Type <strong>"DEACTIVATE"</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deactivateConfirmation}
                  onChange={(e) => setDeactivateConfirmation(e.target.value.toUpperCase())}
                  placeholder='Type "DEACTIVATE" here'
                  className="confirmation-input"
                  disabled={isDeactivating}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-cancel-btn"
                onClick={() => setShowDeactivateModal(false)}
                disabled={isDeactivating}
              >
                Cancel
              </button>
              <button
                className="modal-deactivate-btn"
                onClick={handleDeactivatePharmacy}
                disabled={isDeactivating || deactivateConfirmation !== "DEACTIVATE"}
              >
                {isDeactivating ? "Deactivating..." : "Deactivate Pharmacy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyProfile;